import { defineStore } from 'pinia'
import { ref } from 'vue'
import { gridLayoutService } from '@/services'
import { useCategoriesStore } from '@/stores/categories'
import { useProductsStore } from '@/stores/products'
import { useGridStore } from '@/stores/grid'
import { StorageService, localDb } from '@/services'

const POS_CACHE_KEY = 'pos-data-cache'
const POS_CACHE_SCHEMA_VERSION = 3

/**
 * POS data snapshot store.
 *
 * What we cache:
 * - Categories: `tree` and optional `flat` representation.
 * - Grid products: full register-visible index (drill-eligible + layout-placed refs).
 * - Grid layouts: **all** known layouts keyed by scope plus the active scope.
 * - Catalog revision: server stamp used to bust stale caches across devices.
 */

export const usePosDataStore = defineStore('posData', () => {
  const catalogRevision = ref(null)
  let catalogStampInflight = null
  let catalogStampCache = null
  let catalogStampFetchedAt = 0
  const CATALOG_STAMP_CACHE_MS = 5000

  const saveSnapshot = async () => {
    try {
      const categoriesStore = useCategoriesStore()
      const productsStore = useProductsStore()
      const gridStore = useGridStore()

      const snapshot = {
        schemaVersion: POS_CACHE_SCHEMA_VERSION,
        lastSyncAt: new Date().toISOString(),
        catalogRevision: catalogRevision.value,
        categories: {
          tree: categoriesStore.tree,
          flat: categoriesStore.flat
        },
        gridProducts: productsStore.items,
        layouts: {
          activeScope: gridStore.activeScope,
          all: gridStore.layouts || {}
        }
      }

      await StorageService.set(POS_CACHE_KEY, snapshot)
      try {
        await localDb.cacheSetPosSnapshot(snapshot)
      } catch (e) {
        console.warn('Failed to persist POS snapshot to local DB:', e)
      }
    } catch (e) {
      console.error('Failed to save POS data snapshot:', e)
    }
  }

  const hydrateFromSnapshot = async () => {
    try {
      let snapshot = await StorageService.get(POS_CACHE_KEY)
      if (!snapshot) {
        try {
          snapshot = await localDb.cacheGetPosSnapshot()
        } catch (e) {
          console.warn('Failed to read POS snapshot from local DB:', e)
        }
      }
      if (!snapshot) return

      if (snapshot.schemaVersion !== POS_CACHE_SCHEMA_VERSION) {
        return
      }

      const categoriesStore = useCategoriesStore()
      const productsStore = useProductsStore()
      const gridStore = useGridStore()

      if (snapshot.catalogRevision !== undefined && snapshot.catalogRevision !== null) {
        catalogRevision.value = Number(snapshot.catalogRevision)
      }

      if (snapshot.categories?.tree) {
        categoriesStore.tree = snapshot.categories.tree
        if (snapshot.categories.flat) {
          categoriesStore.flat = snapshot.categories.flat
        }
      }

      const gridProducts = snapshot.gridProducts ?? snapshot.products
      if (Array.isArray(gridProducts)) {
        productsStore.hydrateGridProducts(gridProducts)
        if (snapshot.lastSyncAt) {
          productsStore.lastFetchedAt = Date.parse(snapshot.lastSyncAt) || Date.now()
        }
      }

      if (snapshot.layouts) {
        if (snapshot.layouts.layout && !snapshot.layouts.all) {
          gridStore.layouts = {
            [snapshot.layouts.activeScope || 'user']: snapshot.layouts.layout
          }
        } else if (snapshot.layouts.all) {
          gridStore.layouts = snapshot.layouts.all
        }

        gridStore.activeScope = snapshot.layouts.activeScope || gridStore.activeScope
        gridStore.layout = gridStore.layouts[gridStore.activeScope] || null
        gridStore.pageIndex = 0
        gridStore.stack = []
      }

      gridStore.seedDrillCacheFromCatalog()
    } catch (e) {
      console.error('Failed to hydrate POS data from snapshot:', e)
    }
  }

  const clearSnapshot = async () => {
    try {
      catalogRevision.value = null
      await StorageService.remove(POS_CACHE_KEY)
      try {
        await localDb.cacheSetPosSnapshot(null)
      } catch {
        // ignore
      }
    } catch (e) {
      console.error('Failed to clear POS data snapshot:', e)
    }
  }

  const setCatalogRevision = (revision) => {
    catalogRevision.value = revision !== null && revision !== undefined
      ? Number(revision)
      : null
  }

  const fetchCatalogStamp = async ({ force = false } = {}) => {
    const now = Date.now()
    if (!force && catalogStampInflight) {
      return catalogStampInflight
    }
    if (
      !force &&
      catalogStampCache &&
      (now - catalogStampFetchedAt) < CATALOG_STAMP_CACHE_MS
    ) {
      return catalogStampCache
    }

    catalogStampInflight = (async () => {
      try {
        const stamp = await gridLayoutService.getCatalogStamp()
        catalogStampCache = {
          revision: Number(stamp?.revision ?? 0),
          locationId: stamp?.location_id ?? null
        }
        catalogStampFetchedAt = Date.now()
        return catalogStampCache
      } finally {
        catalogStampInflight = null
      }
    })()

    return catalogStampInflight
  }

  /**
   * Reload categories, grid layouts, and layout-referenced grid products from the server.
   */
  const refreshPosCatalogData = async ({ activeScope = 'user' } = {}) => {
    const categoriesStore = useCategoriesStore()
    const productsStore = useProductsStore()
    const gridStore = useGridStore()

    await categoriesStore.fetchTree({ force: true })

    await gridStore.loadLayout('user', { force: true })
    await gridStore.loadLayout('global', { force: true })
    await gridStore.loadLayout(activeScope)

    await productsStore.preloadGridProducts({
      layouts: gridStore.layouts,
      force: true
    })

    gridStore.seedDrillCacheFromCatalog()

    const stamp = await fetchCatalogStamp()
    setCatalogRevision(stamp.revision)
    await saveSnapshot()

    return stamp
  }

  const seedCatalogStampFromBootstrap = (catalog = null) => {
    if (!catalog || catalog.revision === undefined || catalog.revision === null) {
      return null
    }

    const stamp = {
      revision: Number(catalog.revision),
      locationId: catalog.location_id ?? null
    }
    catalogStampCache = stamp
    catalogStampFetchedAt = Date.now()
    setCatalogRevision(stamp.revision)
    return stamp
  }

  const syncPosCatalogIfStale = async ({ force = false, activeScope = 'user' } = {}) => {
    try {
      const stamp = await fetchCatalogStamp()
      const serverRevision = stamp.revision
      const localRevision = catalogRevision.value

      if (!force && localRevision !== null && localRevision === serverRevision) {
        return { refreshed: false, revision: serverRevision }
      }

      await refreshPosCatalogData({ activeScope })

      return { refreshed: true, revision: serverRevision }
    } catch (e) {
      console.error('Failed to sync POS catalog stamp:', e)
      if (force) {
        throw e
      }
      return { refreshed: false, revision: catalogRevision.value, error: e }
    }
  }

  return {
    catalogRevision,
    setCatalogRevision,
    saveSnapshot,
    hydrateFromSnapshot,
    clearSnapshot,
    fetchCatalogStamp,
    seedCatalogStampFromBootstrap,
    refreshPosCatalogData,
    syncPosCatalogIfStale
  }
})
